import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Target, 
  GraduationCap, 
  Bus, 
  ChevronRight,
  Sparkles,
} from 'lucide-react'
import { Button } from './ui/button'

export type BusinessType = 'sales' | 'school' | 'transport' | null

interface BusinessTypeSelectorProps {
  onSelect: (type: BusinessType) => void
}

const businessOptions = [
  {
    id: 'sales' as const,
    title: 'Sales',
    subtitle: 'CRM & Lead Management',
    description: 'Smart pipelines, client relations, and sales automation for modern businesses.',
    icon: Target,
    gradient: 'from-cyan-500 via-blue-500 to-indigo-600',
    accentColor: 'cyan',
    features: ['Custom Stages', 'Task Management', 'Client CRM', 'Analytics'],
    iconBg: 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20',
    iconColor: 'text-cyan-400',
    borderGlow: 'border-cyan-500/50 shadow-cyan-500/20',
  },
  {
    id: 'school' as const,
    title: 'School',
    subtitle: 'Education Management',
    description: 'Complete school ERP with attendance, bus tracking, parent-teacher meetings, and more.',
    icon: GraduationCap,
    gradient: 'from-amber-500 via-orange-500 to-rose-600',
    accentColor: 'amber',
    features: ['Admissions', 'Parent Portal', 'Bus Tracking', 'Attendance', 'PTM'],
    iconBg: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20',
    iconColor: 'text-amber-400',
    borderGlow: 'border-amber-500/50 shadow-amber-500/20',
  },
  {
    id: 'transport' as const,
    title: 'Transportation',
    subtitle: 'Fleet & Bus Tracking',
    description: 'Real-time vehicle tracking, route optimization, and parent notifications.',
    icon: Bus,
    gradient: 'from-emerald-500 via-teal-500 to-cyan-600',
    accentColor: 'emerald',
    features: ['Live Tracking', 'Route Optimization', 'Parent Alerts', 'Fleet Management'],
    iconBg: 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20',
    iconColor: 'text-emerald-400',
    borderGlow: 'border-emerald-500/50 shadow-emerald-500/20',
  },
]

const BusinessTypeSelector: React.FC<BusinessTypeSelectorProps> = ({ onSelect }) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  return (
    <section className="min-h-screen flex items-center justify-center overflow-hidden relative">
      {/* Top Nav with Login */}
      <nav className="absolute top-0 left-0 right-0 z-50 py-6">
        <div className="container mx-auto px-6 flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/10 backdrop-blur-sm p-1">
              <img src="/logo.svg" alt="VarkWave" className="w-full h-full object-contain" />
            </div>
            <span className="text-2xl font-bold text-white">VarkWave</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-4"
          >
            <Button
              variant="glass"
              size="sm"
              className="border-white/20 text-white hover:bg-white/20"
              onClick={() => (window.location.href = 'https://campus.varkwave.com')}
            >
              Login
            </Button>
          </motion.div>
        </div>
      </nav>

      {/* Animated Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 40%, #1d366e 70%, #0f172a 100%)',
          }}
        />
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-cyan-500 rounded-full mix-blend-screen filter blur-[120px] animate-blob" />
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-indigo-600 rounded-full mix-blend-screen filter blur-[120px] animate-blob animation-delay-2000" />
          <div className="absolute bottom-1/4 left-1/3 w-[550px] h-[550px] bg-blue-500 rounded-full mix-blend-screen filter blur-[120px] animate-blob animation-delay-4000" />
        </div>
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10 py-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-sm font-medium mb-8"
          >
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span>Choose your business type to see custom solutions</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
          >
            What does your organization do?
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-white/60 max-w-2xl mx-auto"
          >
            We'll show you a personalized experience tailored to your industry
          </motion.p>
        </motion.div>

        {/* Business Type Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {businessOptions.map((option, index) => (
            <motion.div
              key={option.id}
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.7, 
                delay: 0.3 + index * 0.15,
                type: "spring",
                stiffness: 80,
                damping: 15,
              }}
              onMouseEnter={() => setHoveredId(option.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="relative group"
            >
              <motion.div
                whileHover={{ 
                  y: -12,
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onSelect(option.id)}
                className={`
                  relative h-full cursor-pointer rounded-3xl p-8
                  bg-white/5 backdrop-blur-xl border-2
                  transition-all duration-500 overflow-hidden
                  ${hoveredId === option.id 
                    ? 'border-white/40 shadow-2xl shadow-cyan-500/20 bg-white/10' 
                    : 'border-white/10 hover:border-white/20'
                  }
                `}
                style={{
                  boxShadow: hoveredId === option.id 
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 40px -10px rgba(6, 182, 212, 0.25)' 
                    : undefined,
                }}
              >
                {/* Hover gradient overlay */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${option.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                  style={{ mixBlendMode: 'screen' }}
                />
                
                {/* Shine effect on hover */}
                <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 1.2 }}
                  />
                </div>

                {/* Icon */}
                <motion.div
                  className={`w-16 h-16 rounded-2xl ${option.iconBg} flex items-center justify-center mb-6`}
                  animate={hoveredId === option.id ? { scale: 1.1, rotate: 5 } : { scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <option.icon className={`w-8 h-8 ${option.iconColor}`} />
                </motion.div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-2">{option.title}</h3>
                <p className={`text-sm font-medium mb-4 ${option.iconColor}`}>{option.subtitle}</p>
                <p className="text-white/60 text-sm mb-6 leading-relaxed">{option.description}</p>

                {/* Feature pills */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {option.features.map((feature, i) => (
                    <motion.span
                      key={feature}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className="px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs font-medium"
                    >
                      {feature}
                    </motion.span>
                  ))}
                </div>

                {/* CTA */}
                <motion.div
                  className="flex items-center gap-2 text-white font-medium"
                  animate={hoveredId === option.id ? { x: 4 } : { x: 0 }}
                >
                  <span>Explore solution</span>
                  <ChevronRight className="w-5 h-5" />
                </motion.div>

                {/* Corner accent */}
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl ${option.gradient} opacity-20 rounded-bl-[100px]`} />
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="flex justify-center mt-16"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/10 backdrop-blur-sm p-1">
              <img src="/logo.svg" alt="VarkWave" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-white">VarkWave</span>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default BusinessTypeSelector
